import dayjs, { Dayjs } from "dayjs"
import type { DataSet } from "@/components/charts/SoLineChart.vue"
import type { EmailChartPoint } from "@/models/admin/emailVolume/emailVolume.parser"
import { BRAND_COLORS, COLOR_PALETTE } from "@/theme/colorPalette"

const OTHER_LABEL = "Other"
const OTHER_COLOR = COLOR_PALETTE.grey600

const TEMPLATE_PALETTE = [
  BRAND_COLORS.sky,
  BRAND_COLORS.fire,
  BRAND_COLORS.sun,
  BRAND_COLORS.earth,
  COLOR_PALETTE.blue700,
  COLOR_PALETTE.red700,
  COLOR_PALETTE.yellow500,
  COLOR_PALETTE.green700,
]

export function bucketEmailsByTemplate(
  records: EmailChartPoint[],
  from: Dayjs,
  to: Dayjs,
  threshold = 10,
  topN = 8,
): { labels: Dayjs[]; dataSets: DataSet[] } {
  const labels: Dayjs[] = []
  let cursor = from.startOf("hour")
  while (cursor.isBefore(to)) {
    labels.push(cursor)
    cursor = cursor.add(1, "hour")
  }

  if (labels.length === 0 || records.length === 0) {
    return { labels, dataSets: [] }
  }

  const buckets = new Map<number, Map<string, number>>()
  const totals = new Map<string, number>()
  for (const r of records) {
    const name = r.communicationName ?? "unknown"
    const hourMs = dayjs(r.processedAt).startOf("hour").valueOf()
    totals.set(name, (totals.get(name) ?? 0) + 1)
    if (!buckets.has(hourMs)) buckets.set(hourMs, new Map())
    const hourBucket = buckets.get(hourMs)!
    hourBucket.set(name, (hourBucket.get(name) ?? 0) + 1)
  }

  const named = [...totals.entries()]
    .filter(([, count]) => count > threshold)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([name]) => name)

  const namedSet = new Set(named)

  const dataSets: DataSet[] = named.map((name, index) => ({
    label: name,
    lineColor: TEMPLATE_PALETTE[index % TEMPLATE_PALETTE.length],
    data: labels.map((label) => buckets.get(label.valueOf())?.get(name) ?? 0),
  }))

  const otherData = labels.map((label) => {
    const hourBucket = buckets.get(label.valueOf())
    if (!hourBucket) return 0
    let count = 0
    for (const [name, c] of hourBucket) {
      if (!namedSet.has(name)) count += c
    }
    return count
  })

  if (otherData.some((c) => c > 0)) {
    dataSets.push({
      label: OTHER_LABEL,
      lineColor: OTHER_COLOR,
      data: otherData,
    })
  }

  return { labels, dataSets }
}
